// src/components/admin/AdminOverview.jsx
import React, { useState, useEffect } from 'react';
import {
    Grid, Paper, Typography, Box, Card, CardContent,
    CardHeader, Divider, List, ListItem, ListItemText,
    CircularProgress, useTheme, Button, Avatar, ListItemAvatar,
    ListItemSecondary, IconButton, Tooltip, Alert, ThemeProvider,
    Skeleton, useMediaQuery
} from '@mui/material';
import {
    PeopleOutline, CategoryOutlined, HomeOutlined,
    SecurityOutlined, BookmarkOutlined, TrendingUpOutlined,
    MoreVert, ArrowForward, NotificationsOutlined,
    ErrorOutline, CheckCircleOutline, AdminPanelSettings,
    VpnKey, PersonOutline, AccountCircle
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { BarChart, Bar, PieChart, Pie, Cell, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend, Area, AreaChart } from 'recharts';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import permissionService from '../../services/permissionService';
import ApartmentIcon from '@mui/icons-material/Apartment';
import EditIcon from '@mui/icons-material/Edit';
import adminTheme from '../../theme/adminTheme';
import DashboardCard from '../common/DashboardCard';

// Styles pour les animations CSS
const fadeInKeyframes = `
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
`;

const AdminOverview = () => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [roles, setRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(true);
    const [avatarLoadErrors, setAvatarLoadErrors] = useState({});
    const [renderAnimations, setRenderAnimations] = useState(false);
    const theme = useTheme();
    const { user } = useAuth();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: { type: 'spring', stiffness: 100 }
        }
    };

    // Colors for charts
    const COLORS = [
        adminTheme.palette.dashboard.users,
        adminTheme.palette.dashboard.categories,
        adminTheme.palette.dashboard.properties,
        adminTheme.palette.dashboard.roles,
        adminTheme.palette.dashboard.permissions,
        adminTheme.palette.dashboard.verifications
    ];
    useEffect(() => {
        // Activer les animations après le montage du composant
        const timer = setTimeout(() => {
            setRenderAnimations(true);
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setLoading(true);
                // Fetch admin stats from API
                const response = await api.get('/admin/stats');
                setStats(response.data);
                setError(null);
            } catch (err) {
                console.error('Error fetching admin stats:', err);
                setError('Impossible de charger les statistiques. Veuillez réessayer plus tard.');

                // Demo data for development/preview
                setStats({
                    userCount: 120,
                    categoryCount: 15,
                    propertyCount: 48,
                    roleCount: 4,
                    permissionCount: 25,
                    pendingVerifications: 7,

                    recentUsers: [
                        { id: 1, name: 'John Doe', email: 'john@example.com', createdAt: '2023-05-15', profilePicture: null },
                        { id: 2, name: 'Jane Smith', email: 'jane@example.com', createdAt: '2023-05-14', profilePicture: null },
                        { id: 3, name: 'Robert Johnson', email: 'robert@example.com', createdAt: '2023-05-13', profilePicture: null },
                        { id: 4, name: 'Emily Davis', email: 'emily@example.com', createdAt: '2023-05-12', profilePicture: null }
                    ],

                    usersByRole: [
                        { name: 'Utilisateurs', count: 80 },
                        { name: 'Locataires', count: 25 },
                        { name: 'Propriétaires', count: 10 },
                        { name: 'Admins', count: 5 }
                    ],

                    recentProperties: [
                        { id: 1, title: 'Appartement moderne', owner: 'John Doe', createdAt: '2023-05-15', status: 'active' },
                        { id: 2, title: 'Maison de campagne', owner: 'Jane Smith', createdAt: '2023-05-14', status: 'pending' },
                        { id: 3, title: 'Studio en centre-ville', owner: 'Robert Johnson', createdAt: '2023-05-13', status: 'active' }
                    ],

                    pendingVerificationsList: [
                        { id: 1, user: 'John Doe', type: 'identity', submittedAt: '2023-05-15' },
                        { id: 2, user: 'Jane Smith', type: 'address', submittedAt: '2023-05-14' },
                        { id: 3, user: 'Robert Johnson', type: 'property', submittedAt: '2023-05-13' }
                    ],

                    activityOverTime: [
                        { month: 'Jan', users: 10, properties: 5 },
                        { month: 'Feb', users: 15, properties: 8 },
                        { month: 'Mar', users: 20, properties: 12 },
                        { month: 'Apr', users: 25, properties: 15 },
                        { month: 'May', users: 30, properties: 20 },
                        { month: 'Jun', users: 35, properties: 25 }
                    ]
                });
            } finally {
                // Délai artificiel pour montrer les skeletons
                setTimeout(() => {
                    setLoading(false);
                }, 800);
            }
        };

        const fetchRoles = async () => {
            try {
                setLoadingRoles(true);
                // Utiliser le service de permissions pour récupérer tous les rôles
                const fetchedRoles = await permissionService.getAllRoles();
                setRoles(fetchedRoles);
            } catch (err) {
                console.error('Error fetching roles:', err);
            } finally {
                // Délai artificiel pour montrer les skeletons
                setTimeout(() => {
                    setLoadingRoles(false);
                }, 1000);
            }
        };

        fetchStats();
        fetchRoles();
    }, []);

// Fonction pour obtenir l'icône correspondant au nom d'icône stocké dans le rôle
    const getIconComponent = (iconName) => {
        switch (iconName) {
            case 'AdminPanelSettings':
                return <AdminPanelSettings />;
            case 'Security':
                return <SecurityOutlined />;
            case 'People':
                return <PeopleOutline />;
            case 'Category':
                return <CategoryOutlined />;
            case 'Edit':
                return <EditIcon />;
            case 'Home':
                return <HomeOutlined />;
            case 'Apartment':
                return <ApartmentIcon />;
            default:
                return <VpnKey />;
        }
    };

// Fonction pour gérer les erreurs de chargement d'avatar
    const handleAvatarError = (userId) => {
        setAvatarLoadErrors(prev => ({
            ...prev,
            [userId]: true
        }));
    };

// Fonction pour générer un avatar de secours basé sur le nom
    const getInitialsAvatar = (name) => {
        if (!name) return <PersonOutline />;
        return name.charAt(0).toUpperCase();
    };

// Fonction pour obtenir la couleur d'avatar basée sur l'ID
    const getAvatarColor = (id) => {
        const colors = [
            adminTheme.palette.primary.main,
            adminTheme.palette.secondary.main,
            adminTheme.palette.success.main,
            adminTheme.palette.warning.main,
            adminTheme.palette.info.main,
        ];
        return colors[id % colors.length];
    };

// Fonction personnalisée pour formater les tooltips des graphiques
    const customTooltipFormatter = (value, name, props) => {
        const labels = {
            users: 'Utilisateurs',
            properties: 'Propriétés'
        };
        return [`${value}`, labels[name] || name];
    };

// Fonction pour le rendu personnalisé des tooltips
    const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
            return (
                <Box
                    sx={{
                        backgroundColor: adminTheme.palette.background.paper,
                        border: `1px solid ${adminTheme.palette.divider}`,
                        borderRadius: 2,
                        p: 1.5,
                        boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)'
                    }}
                >
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
                        {label}
                    </Typography>
                    {payload.map((entry, index) => (
                        <Box
                            key={`tooltip-item-${index}`}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 0.5
                            }}
                        >
                            <Box
                                component="span"
                                sx={{
                                    width: 12,
                                    height: 12,
                                    borderRadius: '50%',
                                    backgroundColor: entry.color,
                                    mr: 1
                                }}
                            />
                            <Typography variant="body2" sx={{ color: adminTheme.palette.text.primary }}>
                                {entry.name === 'users' ? 'Utilisateurs' : entry.name === 'properties' ? 'Propriétés' : entry.name}: <strong>{entry.value}</strong>
                            </Typography>
                        </Box>
                    ))}
                </Box>
            );
        }
        return null;
    };

// Rendu des skeletons pour les états de chargement
    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                {/* Header skeleton */}
                <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Skeleton variant="text" width={300} height={40} />
                    <Box sx={{ display: 'flex' }}>
                        <Skeleton variant="rounded" width={150} height={40} sx={{ mr: 2 }} />
                        <Skeleton variant="rounded" width={180} height={40} />
                    </Box>
                </Box>

                {/* Welcome message skeleton */}
                <Skeleton variant="rounded" width="100%" height={100} sx={{ mb: 4 }} />

                {/* Stats cards skeletons */}
                <Skeleton variant="text" width={200} height={30} sx={{ mb: 3 }} />
                <Grid container spacing={3} sx={{ mb: 5 }}>
                    {[...Array(6)].map((_, index) => (
                        <Grid item xs={12} sm={6} md={4} lg={2} key={index}>
                            <Skeleton variant="rounded" width="100%" height={160} />
                        </Grid>
                    ))}
                </Grid>

                {/* Charts skeletons */}
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Skeleton variant="rounded" width="100%" height={400} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Skeleton variant="rounded" width="100%" height={400} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Skeleton variant="rounded" width="100%" height={400} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Skeleton variant="rounded" width="100%" height={400} />
                    </Grid>
                </Grid>
            </Box>
        );
    }
    return (
        <ThemeProvider theme={adminTheme}>
            {/* Styles CSS pour les animations */}
            <style>{fadeInKeyframes}</style>

            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                <Box sx={{ p: 3 }}>
                    <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography
                            variant="h4"
                            component="h1"
                            gutterBottom
                            fontWeight="600"
                            sx={{
                                color: adminTheme.palette.text.primary,
                                mb: { xs: 2, md: 0 }
                            }}
                        >
                            Tableau de bord d'administration
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <Tooltip title={`${stats?.pendingVerifications || 0} notifications en attente`}>
                                <Button
                                    variant="outlined"
                                    startIcon={<NotificationsOutlined />}
                                    aria-label={`Notifications (${stats?.pendingVerifications || 0})`}
                                    sx={{
                                        borderRadius: 2,
                                        position: 'relative',
                                        '&:focus-visible': {
                                            outline: `2px solid ${adminTheme.palette.primary.main}`,
                                            outlineOffset: 2
                                        }
                                    }}
                                >
                                    Notifications
                                    {stats?.pendingVerifications > 0 && (
                                        <Box
                                            component="span"
                                            sx={{
                                                ml: 1,
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                bgcolor: adminTheme.palette.error.main,
                                                color: 'white',
                                                borderRadius: '50%',
                                                width: 20,
                                                height: 20,
                                                fontSize: '0.75rem',
                                                fontWeight: 'bold',
                                                animation: 'fadeIn 0.3s ease-in-out'
                                            }}
                                        >
                                            {stats.pendingVerifications}
                                        </Box>
                                    )}
                                </Button>
                            </Tooltip>
                            <Button
                                variant="contained"
                                component={Link}
                                to="/admin/settings"
                                startIcon={<SecurityOutlined />}
                                aria-label="Paramètres système"
                                sx={{
                                    borderRadius: 2,
                                    '&:focus-visible': {
                                        outline: `2px solid ${adminTheme.palette.primary.main}`,
                                        outlineOffset: 2
                                    }
                                }}
                            >
                                Paramètres système
                            </Button>
                        </Box>
                    </Box>

                    {error && (
                        <Alert
                            severity="error"
                            sx={{
                                mb: 3,
                                opacity: 1,
                                animation: 'fadeIn 0.3s ease-in-out'
                            }}
                            action={
                                <Button color="inherit" size="small" onClick={() => setError(null)}>
                                    Fermer
                                </Button>
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Welcome message */}
                    <motion.div variants={itemVariants}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 3,
                                mb: 4,
                                background: `linear-gradient(135deg, ${adminTheme.palette.primary.main} 30%, ${adminTheme.palette.primary.dark} 90%)`,
                                color: 'white',
                                borderRadius: 2,
                                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 0,
                                    right: 0,
                                    width: { xs: 100, md: 150 },
                                    height: { xs: 100, md: 150 },
                                    background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
                                    borderRadius: '0 0 0 100%'
                                }}
                            />
                            <Box sx={{ position: 'relative', zIndex: 1 }}>
                                <Typography variant="h5" gutterBottom fontWeight="500">
                                    Bienvenue, {user?.name || 'Administrateur'} !
                                </Typography>
                                <Typography variant="body1">
                                    Voici un aperçu de l'activité récente et des statistiques importantes de votre plateforme.
                                    {stats?.pendingVerifications > 0 && (
                                        <Box component="span" sx={{ fontWeight: 'bold' }}>
                                            {` Vous avez ${stats.pendingVerifications} vérifications en attente.`}
                                        </Box>
                                    )}
                                </Typography>
                            </Box>
                        </Paper>
                    </motion.div>

                    {/* Stats cards */}
                    <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                            mb: 3,
                            fontWeight: 500,
                            color: adminTheme.palette.text.primary,
                            position: 'relative',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: -8,
                                left: 0,
                                width: 40,
                                height: 3,
                                backgroundColor: adminTheme.palette.primary.main,
                                borderRadius: 1.5
                            }
                        }}
                    >
                        Statistiques générales
                    </Typography>
                    <Grid container spacing={3} sx={{ mb: 5 }}>
                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <motion.div variants={itemVariants}>
                                <DashboardCard
                                    icon={<PeopleOutline />}
                                    title="Utilisateurs"
                                    value={stats?.userCount || 0}
                                    linkTo="/admin/users"
                                    color={adminTheme.palette.dashboard.users}
                                    buttonText="Gérer"
                                    aria-label="Gérer les utilisateurs"
                                />
                            </motion.div>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <motion.div variants={itemVariants}>
                                <DashboardCard
                                    icon={<CategoryOutlined />}
                                    title="Catégories"
                                    value={stats?.categoryCount || 0}
                                    linkTo="/admin/categories"
                                    color={adminTheme.palette.dashboard.categories}
                                    buttonText="Gérer"
                                    aria-label="Gérer les catégories"
                                />
                            </motion.div>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <motion.div variants={itemVariants}>
                                <DashboardCard
                                    icon={<HomeOutlined />}
                                    title="Propriétés"
                                    value={stats?.propertyCount || 0}
                                    linkTo="/admin/properties"
                                    color={adminTheme.palette.dashboard.properties}
                                    buttonText="Gérer"
                                    aria-label="Gérer les propriétés"
                                />
                            </motion.div>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <motion.div variants={itemVariants}>
                                <DashboardCard
                                    icon={<SecurityOutlined />}
                                    title="Rôles"
                                    value={stats?.roleCount || roles.length || 0}
                                    linkTo="/admin/roles"
                                    color={adminTheme.palette.dashboard.roles}
                                    buttonText="Gérer"
                                    aria-label="Gérer les rôles"
                                />
                            </motion.div>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <motion.div variants={itemVariants}>
                                <DashboardCard
                                    icon={<BookmarkOutlined />}
                                    title="Permissions"
                                    value={stats?.permissionCount || 0}
                                    linkTo="/admin/permissions"
                                    color={adminTheme.palette.dashboard.permissions}
                                    buttonText="Gérer"
                                    aria-label="Gérer les permissions"
                                />
                            </motion.div>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4} lg={2}>
                            <motion.div variants={itemVariants}>
                                <DashboardCard
                                    icon={<ErrorOutline />}
                                    title="Vérifications"
                                    value={stats?.pendingVerifications || 0}
                                    linkTo="/admin/verifications"
                                    color={adminTheme.palette.dashboard.verifications}
                                    buttonText="Gérer"
                                    aria-label="Gérer les vérifications"
                                />
                            </motion.div>
                        </Grid>
                    </Grid>
                    {/* Charts and lists */}
                    <Grid container spacing={3}>
                        {/* User distribution chart - AMÉLIORÉ */}
                        <Grid item xs={12} md={6}>
                            <motion.div variants={itemVariants}>
                                <Paper elevation={3} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h6" fontWeight="500">
                                            Répartition des utilisateurs
                                        </Typography>
                                        <Tooltip title="Voir tous les utilisateurs">
                                            <IconButton
                                                component={Link}
                                                to="/admin/users"
                                                size="small"
                                                aria-label="Voir tous les utilisateurs"
                                                sx={{
                                                    '&:focus-visible': {
                                                        outline: `2px solid ${adminTheme.palette.primary.main}`,
                                                        outlineOffset: 2
                                                    }
                                                }}
                                            >
                                                <ArrowForward />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <Pie
                                                data={stats?.usersByRole || []}
                                                cx="50%"
                                                cy="50%"
                                                labelLine={true}
                                                outerRadius={100}
                                                innerRadius={60}
                                                fill="#8884d8"
                                                dataKey="count"
                                                nameKey="name"
                                                paddingAngle={2}
                                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                            >
                                                {stats?.usersByRole?.map((entry, index) => (
                                                    <Cell
                                                        key={`cell-${index}`}
                                                        fill={COLORS[index % COLORS.length]}
                                                        stroke={adminTheme.palette.background.paper}
                                                        strokeWidth={2}
                                                    />
                                                ))}
                                            </Pie>
                                            <Legend
                                                layout="horizontal"
                                                verticalAlign="bottom"
                                                align="center"
                                                formatter={(value, entry, index) => (
                                                    <span style={{
                                                        color: COLORS[index % COLORS.length],
                                                        fontWeight: 500,
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        backgroundColor: `${COLORS[index % COLORS.length]}10`
                                                    }}>
                            {value} ({stats?.usersByRole[index]?.count || 0})
                          </span>
                                                )}
                                                iconSize={10}
                                                iconType="circle"
                                                wrapperStyle={{ paddingTop: 20 }}
                                            />
                                            <RechartsTooltip
                                                formatter={(value, name, props) => {
                                                    const total = stats?.userCount || 0;
                                                    const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                                                    return [`${value} utilisateurs (${percentage}%)`, name];
                                                }}
                                                contentStyle={{
                                                    backgroundColor: adminTheme.palette.background.paper,
                                                    border: `1px solid ${adminTheme.palette.divider}`,
                                                    borderRadius: 8,
                                                    padding: 10,
                                                    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.1)'
                                                }}
                                                itemStyle={{ color: adminTheme.palette.text.primary }}
                                                labelStyle={{ fontWeight: 'bold', marginBottom: 5 }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                                        <Typography variant="body2" color="text.secondary" align="center">
                                            Total: {stats?.userCount || 0} utilisateurs
                                        </Typography>
                                    </Box>
                                </Paper>
                            </motion.div>
                        </Grid>

                        {/* Activity over time chart - AMÉLIORÉ */}
                        <Grid item xs={12} md={6}>
                            <motion.div variants={itemVariants}>
                                <Paper elevation={3} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h6" fontWeight="500">
                                            Activité au fil du temps
                                        </Typography>
                                        <Tooltip title="Voir les statistiques détaillées">
                                            <IconButton
                                                component={Link}
                                                to="/admin/statistics"
                                                size="small"
                                                aria-label="Voir les statistiques détaillées"
                                                sx={{
                                                    '&:focus-visible': {
                                                        outline: `2px solid ${adminTheme.palette.primary.main}`,
                                                        outlineOffset: 2
                                                    }
                                                }}
                                            >
                                                <ArrowForward />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <AreaChart
                                            data={stats?.activityOverTime || []}
                                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                                        >
                                            <defs>
                                                <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={adminTheme.palette.dashboard.users} stopOpacity={0.8}/>
                                                    <stop offset="95%" stopColor={adminTheme.palette.dashboard.users} stopOpacity={0.1}/>
                                                </linearGradient>
                                                <linearGradient id="colorProperties" x1="0" y1="0" x2="0" y2="1">
                                                    <stop offset="5%" stopColor={adminTheme.palette.dashboard.properties} stopOpacity={0.8}/>
                                                    <stop offset="95%" stopColor={adminTheme.palette.dashboard.properties} stopOpacity={0.1}/>
                                                </linearGradient>
                                            </defs>
                                            <XAxis
                                                dataKey="month"
                                                tick={{ fill: adminTheme.palette.text.secondary }}
                                                axisLine={{ stroke: adminTheme.palette.divider }}
                                                tickLine={{ stroke: adminTheme.palette.divider }}
                                            />
                                            <YAxis
                                                tick={{ fill: adminTheme.palette.text.secondary }}
                                                axisLine={{ stroke: adminTheme.palette.divider }}
                                                tickLine={{ stroke: adminTheme.palette.divider }}
                                            />
                                            <CartesianGrid strokeDasharray="3 3" stroke={adminTheme.palette.divider} />
                                            <RechartsTooltip content={<CustomTooltip />} />
                                            <Legend
                                                formatter={(value, entry) => {
                                                    const labels = {
                                                        users: 'Utilisateurs',
                                                        properties: 'Propriétés'
                                                    };
                                                    return (
                                                        <span style={{
                                                            color: entry.color,
                                                            fontWeight: 500,
                                                            padding: '4px 8px',
                                                            borderRadius: '4px',
                                                            backgroundColor: `${entry.color}10`
                                                        }}>
                              {labels[value] || value}
                            </span>
                                                    );
                                                }}
                                                iconType="circle"
                                                iconSize={10}
                                                wrapperStyle={{ paddingTop: 10 }}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="users"
                                                stroke={adminTheme.palette.dashboard.users}
                                                fillOpacity={1}
                                                fill="url(#colorUsers)"
                                                activeDot={{ r: 8, strokeWidth: 0 }}
                                                strokeWidth={2}
                                            />
                                            <Area
                                                type="monotone"
                                                dataKey="properties"
                                                stroke={adminTheme.palette.dashboard.properties}
                                                fillOpacity={1}
                                                fill="url(#colorProperties)"
                                                activeDot={{ r: 8, strokeWidth: 0 }}
                                                strokeWidth={2}
                                            />
                                        </AreaChart>
                                    </ResponsiveContainer>
                                </Paper>
                            </motion.div>
                        </Grid>

                        {/* Recent users list */}
                        <Grid item xs={12} md={6}>
                            <motion.div variants={itemVariants}>
                                <Paper elevation={3} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h6" fontWeight="500">
                                            Utilisateurs récents
                                        </Typography>
                                        <Tooltip title="Voir tous les utilisateurs">
                                            <IconButton
                                                component={Link}
                                                to="/admin/users"
                                                size="small"
                                                aria-label="Voir tous les utilisateurs"
                                                sx={{
                                                    '&:focus-visible': {
                                                        outline: `2px solid ${adminTheme.palette.primary.main}`,
                                                        outlineOffset: 2
                                                    }
                                                }}
                                            >
                                                <ArrowForward />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <List sx={{ px: 1 }}>
                                        {stats?.recentUsers?.map((user, index) => (
                                            <React.Fragment key={user.id}>
                                                <ListItem
                                                    sx={{
                                                        borderRadius: 2,
                                                        transition: 'all 0.2s ease-in-out',
                                                        '&:hover': {
                                                            backgroundColor: 'rgba(0,0,0,0.04)',
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                                                        },
                                                        opacity: 1,
                                                        animation: `fadeIn 0.5s ease-in-out ${index * 0.1}s`
                                                    }}
                                                >
                                                    <ListItemAvatar>
                                                        {user.profilePicture && !avatarLoadErrors[user.id] ? (
                                                            <Avatar
                                                                src={user.profilePicture}
                                                                alt={`Photo de profil de ${user.name}`}
                                                                onError={() => handleAvatarError(user.id)}
                                                                sx={{
                                                                    bgcolor: getAvatarColor(user.id),
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                                }}
                                                            />
                                                        ) : (
                                                            <Avatar
                                                                sx={{
                                                                    bgcolor: getAvatarColor(user.id),
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                                                                }}
                                                                aria-label={`Initiale de ${user.name}`}
                                                            >
                                                                {getInitialsAvatar(user.name)}
                                                            </Avatar>
                                                        )}
                                                    </ListItemAvatar>
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="subtitle1" fontWeight="500">
                                                                {user.name}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                                                                <Typography
                                                                    variant="body2"
                                                                    component="span"
                                                                    sx={{ color: adminTheme.palette.text.secondary }}
                                                                >
                                                                    {user.email}
                                                                </Typography>
                                                                <Typography
                                                                    variant="caption"
                                                                    component="span"
                                                                    sx={{
                                                                        color: adminTheme.palette.text.secondary,
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        mt: 0.5
                                                                    }}
                                                                >
                                                                    Inscrit le {new Date(user.createdAt).toLocaleDateString()}
                                                                </Typography>
                                                            </Box>
                                                        }
                                                    />
                                                    <Tooltip title="Voir le profil">
                                                        <IconButton
                                                            component={Link}
                                                            to={`/admin/users/${user.id}`}
                                                            size="small"
                                                            aria-label={`Voir le profil de ${user.name}`}
                                                            sx={{
                                                                color: adminTheme.palette.dashboard.users,
                                                                transition: 'all 0.2s ease-in-out',
                                                                '&:hover': {
                                                                    backgroundColor: `${adminTheme.palette.dashboard.users}15`,
                                                                    transform: 'scale(1.1)'
                                                                },
                                                                '&:focus-visible': {
                                                                    outline: `2px solid ${adminTheme.palette.primary.main}`,
                                                                    outlineOffset: 2
                                                                }
                                                            }}
                                                        >
                                                            <ArrowForward />
                                                        </IconButton>
                                                    </Tooltip>
                                                </ListItem>
                                                <Divider component="li" variant="inset" />
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </Paper>
                            </motion.div>
                        </Grid>

                        {/* Roles list */}
                        <Grid item xs={12} md={6}>
                            <motion.div variants={itemVariants}>
                                <Paper elevation={3} sx={{ p: 3, height: '100%', borderRadius: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                                        <Typography variant="h6" fontWeight="500">
                                            Rôles du système
                                        </Typography>
                                        <Tooltip title="Gérer les rôles">
                                            <IconButton
                                                component={Link}
                                                to="/admin/roles"
                                                size="small"
                                                aria-label="Gérer les rôles"
                                                sx={{
                                                    color: adminTheme.palette.dashboard.roles,
                                                    '&:hover': {
                                                        backgroundColor: `${adminTheme.palette.dashboard.roles}15`
                                                    },
                                                    '&:focus-visible': {
                                                        outline: `2px solid ${adminTheme.palette.primary.main}`,
                                                        outlineOffset: 2
                                                    }
                                                }}
                                            >
                                                <ArrowForward />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    {loadingRoles ? (
                                        <Box sx={{ p: 2 }}>
                                            {[...Array(5)].map((_, index) => (
                                                <Box key={index} sx={{ mb: 2 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                                        <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                                                        <Box sx={{ width: '100%' }}>
                                                            <Skeleton variant="text" width="60%" height={24} />
                                                            <Skeleton variant="text" width="90%" height={20} />
                                                        </Box>
                                                    </Box>
                                                    <Divider />
                                                </Box>
                                            ))}
                                        </Box>
                                    ) : roles.length > 0 ? (
                                        <List sx={{ px: 1 }}>
                                            {roles.slice(0, 5).map((role, index) => (
                                                <React.Fragment key={role.id}>
                                                    <ListItem
                                                        sx={{
                                                            borderRadius: 2,
                                                            transition: 'all 0.2s ease-in-out',
                                                            '&:hover': {
                                                                backgroundColor: 'rgba(0,0,0,0.04)',
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                                                            },
                                                            opacity: 1,
                                                            animation: `fadeIn 0.5s ease-in-out ${index * 0.1}s`
                                                        }}
                                                    >
                                                        <ListItemAvatar>
                                                            <Avatar
                                                                sx={{
                                                                    bgcolor: role.color || adminTheme.palette.dashboard.roles,
                                                                    color: 'white',
                                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                                                    transition: 'all 0.2s ease-in-out',
                                                                    '&:hover': {
                                                                        transform: 'scale(1.1)'
                                                                    }
                                                                }}
                                                                aria-label={`Icône du rôle ${role.name}`}
                                                            >
                                                                {getIconComponent(role.icon)}
                                                            </Avatar>
                                                        </ListItemAvatar>
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="subtitle1" fontWeight="500">
                                                                    {role.name}
                                                                </Typography>
                                                            }
                                                            secondary={
                                                                <>
                                                                    <Typography
                                                                        variant="body2"
                                                                        component="span"
                                                                        sx={{
                                                                            color: adminTheme.palette.text.secondary,
                                                                            display: 'block',
                                                                            mb: 0.5
                                                                        }}
                                                                    >
                                                                        {role.description || 'Aucune description'}
                                                                    </Typography>
                                                                    <Box component="span" sx={{
                                                                        display: 'inline-flex',
                                                                        alignItems: 'center',
                                                                        mt: 0.5,
                                                                        color: role.isActive ? 'success.main' : 'text.secondary',
                                                                        fontSize: '0.75rem',
                                                                        padding: '2px 8px',
                                                                        borderRadius: 10,
                                                                        backgroundColor: role.isActive
                                                                            ? `${adminTheme.palette.success.main}15`
                                                                            : `${adminTheme.palette.error.main}15`
                                                                    }}>
                                                                        {role.isSystem ? 'Rôle système' : 'Rôle personnalisé'} •
                                                                        <Box component="span" sx={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            ml: 1,
                                                                            color: role.isActive ? 'success.main' : 'error.main'
                                                                        }}>
                                                                            {role.isActive ? (
                                                                                <CheckCircleOutline fontSize="small" sx={{ mr: 0.5, fontSize: '0.875rem' }} />
                                                                            ) : (
                                                                                <ErrorOutline fontSize="small" sx={{ mr: 0.5, fontSize: '0.875rem' }} />
                                                                            )}
                                                                            {role.isActive ? 'Actif' : 'Inactif'}
                                                                        </Box>
                                                                    </Box>
                                                                </>
                                                            }
                                                        />
                                                        <Tooltip title="Gérer ce rôle">
                                                            <IconButton
                                                                component={Link}
                                                                to={`/admin/roles/edit/${role.id}`}
                                                                size="small"
                                                                aria-label={`Gérer le rôle ${role.name}`}
                                                                sx={{
                                                                    color: adminTheme.palette.dashboard.roles,
                                                                    transition: 'all 0.2s ease-in-out',
                                                                    '&:hover': {
                                                                        backgroundColor: `${adminTheme.palette.dashboard.roles}15`,
                                                                        transform: 'scale(1.1)'
                                                                    },
                                                                    '&:focus-visible': {
                                                                        outline: `2px solid ${adminTheme.palette.primary.main}`,
                                                                        outlineOffset: 2
                                                                    }
                                                                }}
                                                            >
                                                                <ArrowForward />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </ListItem>
                                                    <Divider component="li" variant="inset" />
                                                </React.Fragment>
                                            ))}
                                            {roles.length > 5 && (
                                                <Box
                                                    sx={{
                                                        textAlign: 'center',
                                                        mt: 2,
                                                        opacity: 1,
                                                        animation: 'fadeIn 0.5s ease-in-out 0.5s'
                                                    }}
                                                >
                                                    <Button
                                                        component={Link}
                                                        to="/admin/roles"
                                                        variant="outlined"
                                                        color="primary"
                                                        aria-label={`Voir tous les ${roles.length} rôles`}
                                                        sx={{
                                                            borderRadius: 2,
                                                            mt: 1,
                                                            color: adminTheme.palette.dashboard.roles,
                                                            borderColor: adminTheme.palette.dashboard.roles,
                                                            transition: 'all 0.2s ease-in-out',
                                                            '&:hover': {
                                                                borderColor: adminTheme.palette.dashboard.roles,
                                                                backgroundColor: `${adminTheme.palette.dashboard.roles}10`,
                                                                transform: 'translateY(-2px)',
                                                                boxShadow: '0 4px 10px rgba(0,0,0,0.05)'
                                                            },
                                                            '&:focus-visible': {
                                                                outline: `2px solid ${adminTheme.palette.primary.main}`,
                                                                outlineOffset: 2
                                                            }
                                                        }}
                                                    >
                                                        Voir tous les rôles ({roles.length})
                                                    </Button>
                                                </Box>
                                            )}
                                        </List>
                                    ) : (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                Aucun rôle trouvé
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                            </motion.div>
                        </Grid>
                    </Grid>
                </Box>
            </motion.div>
        </ThemeProvider>
    );
};

export default AdminOverview;